import React, { useState, useEffect } from 'react';
import { getSlides, publishSlides, deleteSlide } from '../../services/slideService';
import './SlidePreview.css';

const SlidePreview = ({ courseId, moduleId, topicId, onEdit }) => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    loadSlides();
  }, [courseId, moduleId, topicId]);

  const loadSlides = async () => {
    try {
      const data = await getSlides(courseId, moduleId, topicId);
      setSlides(data);
    } catch (error) {
      console.error('Failed to load slides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (window.confirm('Publish all slides? Students will see them.')) {
      try {
        await publishSlides(courseId, moduleId, topicId);
        alert('Slides published!');
        loadSlides();
      } catch (error) {
        alert('Failed to publish: ' + error.message);
      }
    }
  };

  const handleDelete = async (slideId) => {
    if (window.confirm('Delete this slide?')) {
      try {
        await deleteSlide(slideId);
        alert('Slide deleted!');
        loadSlides();
        if (currentIndex >= slides.length - 1) {
          setCurrentIndex(Math.max(0, currentIndex - 1));
        }
      } catch (error) {
        alert('Failed to delete: ' + error.message);
      }
    }
  };

  if (loading) return <div className="loading">Loading slides...</div>;
  if (slides.length === 0) return <div className="no-slides">No slides yet. Create some!</div>;

  const currentSlide = slides[currentIndex];

  return (
    <div className="slide-preview">
      <div className="preview-header">
        <h2>Slide Preview ({slides.length} slides)</h2>
        <button onClick={handlePublish} className="btn-primary">Publish All</button>
      </div>

      <div className="slide-display">
        <button
          onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
          className="nav-btn prev"
        >
          ‹
        </button>

        <div className="slide-content">
          <div className={`slide slide-${currentSlide.template_type}`}>
            {currentSlide.header && <h1>{currentSlide.header}</h1>}
            {currentSlide.subheader && <h2>{currentSlide.subheader}</h2>}
            {currentSlide.content_section_1 && <p>{currentSlide.content_section_1}</p>}
            {currentSlide.bullet_points && currentSlide.bullet_points.length > 0 && (
              <ul>
                {currentSlide.bullet_points.map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>
            )}
            {currentSlide.content_section_2 && (
              <div className="section-2">
                <p>{currentSlide.content_section_2}</p>
              </div>
            )}
            {currentSlide.code_snippet && (
              <pre><code>{currentSlide.code_snippet}</code></pre>
            )}
            {currentSlide.visual_url && (
              <img src={currentSlide.visual_url} alt="Visual" />
            )}
          </div>
          <div className="slide-info">
            Slide {currentIndex + 1} of {slides.length} | {currentSlide.template_type}
          </div>
        </div>

        <button
          onClick={() => setCurrentIndex(Math.min(slides.length - 1, currentIndex + 1))}
          disabled={currentIndex === slides.length - 1}
          className="nav-btn next"
        >
          ›
        </button>
      </div>

      <div className="slide-actions">
        <button onClick={() => onEdit && onEdit(currentSlide)} className="btn-secondary">
          Edit Slide
        </button>
        <button onClick={() => handleDelete(currentSlide.id)} className="btn-danger">
          Delete Slide
        </button>
      </div>

      <div className="slide-thumbnails">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
          >
            <div className="thumb-number">{index + 1}</div>
            <div className="thumb-title">{slide.header || 'Untitled'}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SlidePreview;
